import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Awin - This comment is for verification purposes of our affiliate account
interface GoogleAnalyticsProps {
  measurementId: string;  // Your GA measurement ID (e.g., "G-XXXXXXXXXX")
  enabled: boolean;  // Whether analytics is enabled based on cookie preferences
  debug?: boolean;   // Enable debug logging
}

// Event tracking interface
export interface AnalyticsEvent {
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

/**
 * GoogleAnalytics component that conditionally loads Google Analytics
 * based on user's cookie preferences
 */
const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ 
  measurementId, 
  enabled,
  debug = false 
}) => {
  const location = useLocation();
  const initializedRef = useRef(false);
  const consentUpdatedRef = useRef(false);
  const pageViewTrackedRef = useRef(false);
  
  // Handle configuration once when component mounts - do this first
  useEffect(() => {
    if (!window.gtag || !measurementId || initializedRef.current) return;
    
    try {
      // Check if GA has already been initialized with this ID to prevent duplicate initialization
      const alreadyInitialized = window.dataLayer?.some(item => 
        Array.isArray(item) && 
        item[0] === 'config' && 
        item[1] === measurementId
      );
      
      if (alreadyInitialized) {
        initializedRef.current = true;
        if (debug) {
          console.log('Google Analytics already configured with measurementId:', measurementId);
        }
        return;
      }
      
      // Mark as initialized to prevent duplicate initialization
      initializedRef.current = true;
      
      // Configure Google Analytics with best practices and consistent cookie settings
      window.gtag('config', measurementId, {
        send_page_view: false, // We'll handle page views separately
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
        cookie_prefix: '_ga',
        cookie_domain: window.location.hostname,
        cookie_expires: 63072000, // 2 years in seconds, matching GA4 default
        cookie_update: false // This is the key setting to prevent cookie overwrites
      });
      
      if (debug) {
        console.log('Google Analytics configured with measurementId:', measurementId);
      }
    } catch (error) {
      console.error('Error configuring Google Analytics:', error);
    }
  }, [measurementId, debug]);
  
  // Handle consent updates only after initialization
  useEffect(() => {
    if (!window.gtag || !measurementId || !initializedRef.current) return;
    
    // Only update consent when enabled status changes
    if (consentUpdatedRef.current !== enabled) {
      consentUpdatedRef.current = enabled;
      
      // Update consent state based on user preferences
      window.gtag('consent', 'update', {
        'analytics_storage': enabled ? 'granted' : 'denied',
        'ad_storage': enabled ? 'granted' : 'denied'
      });
      
      // Log for debugging
      if (debug) {
        console.log(`Google Analytics consent updated: analytics_storage=${enabled ? 'granted' : 'denied'}`);
      }
    }
  }, [enabled, measurementId, debug]);

  // Track page views when location changes - do this last
  useEffect(() => {
    // Reset page view tracking on location change
    if (location) {
      pageViewTrackedRef.current = false;
    }
    
    if (!enabled || !window.gtag || !measurementId || !initializedRef.current) return;
    
    // Prevent duplicate page views for the same path
    if (pageViewTrackedRef.current) return;
    
    try {
      // Send page view event
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
        send_to: measurementId // Explicitly specify where to send this event
      });
      
      pageViewTrackedRef.current = true;
      
      if (debug) {
        console.log('Page view tracked:', location.pathname);
      }
    } catch (error) {
      console.error('Error sending page view:', error);
    }
  }, [location, enabled, measurementId, debug]);

  return null;
};

// Declare global gtag function
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Export a helper function for tracking custom events
export const trackEvent = (
  eventName: string, 
  eventParams?: Record<string, any>
) => {
  if (window.gtag && typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
};

// Export a helper function to track e-commerce events
export const trackEcommerce = {
  viewItem: (item: { id: string, name: string, price?: number, category?: string }) => {
    if (window.gtag) {
      window.gtag('event', 'view_item', {
        items: [{
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          item_category: item.category
        }]
      });
    }
  },
  
  beginCheckout: (items: Array<{ id: string, name: string, price?: number, quantity?: number }>) => {
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        items: items.map(item => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity || 1
        }))
      });
    }
  }
};

export default GoogleAnalytics;